import ContentLoadingState from "@/components/ContentLoadingState";
import React from "react";
import { useParams, Link, useNavigate, useLocation } from "@/lib/router-compat";
import { PullQuote } from "@/components/content/PullQuote";
import VideoChapters from "@/components/content/VideoChapters";
import { useQuery } from "@tanstack/react-query";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";
import { localizeContentRow } from "@/lib/localizeContent";
import { ExternalLink, Mic, User, ArrowRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { JsonLd } from "@/components/PageHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import BlogSEO from "@/components/blog/BlogSEO";
import ReadingProgressBar from "@/components/blog/ReadingProgressBar";
import TableOfContents from "@/components/blog/TableOfContents";
import ShareButtons from "@/components/blog/ShareButtons";
import NewsletterSignup from "@/components/blog/NewsletterSignup";
import AuthorBioBox from "@/components/blog/AuthorBioBox";

import ListenButton from "@/components/blog/ListenButton";
import ContentPostHero from "@/components/content/ContentPostHero";
import RelatedContent from "@/components/content/RelatedContent";
import CaseStudyLayout from "@/components/content/CaseStudyLayout";

import SplitBanner from "@/components/ui/SplitBanner";
import RegulatoryUpdateContent from "@/components/content/RegulatoryUpdateContent";
import BlogPostContent from "@/components/content/BlogPostContent";
import WebinarRegistrationForm from "@/components/webinar/WebinarRegistrationForm";
import { supabase } from "@/integrations/supabase/client";
import { cleanBlogContent } from "@/lib/cleanBlogContent";
import { injectInternalLinks } from "@/lib/internalLinks";
import { parseCaseStudyContent, extractCaseStudyMeta } from "@/lib/parseCaseStudyContent";
import { authorSlug, authorDisplayName, parseAuthors } from "@/lib/authorSlug";
import { getContentTypeMeta, type ContentTypeKey } from "@/lib/resourceTypes";
import { extractHowToSteps } from "@/lib/extractHowToSteps";
import InlineCTA from "@/components/blog/InlineCTA";
import CrossContentCards from "@/components/content/CrossContentCards";
import { localizeHref } from "@/lib/localizeHref";
import vlmcareHeroVisual from "@/assets/vlmcare-article-header.png.asset.json";
import vlmcareLogoWhite from "@/assets/VLMcare_logo_white.svg.asset.json";
import bjornAalbersPhoto from "@/assets/bjorn-aalbers.png.asset.json";
import { Linkedin } from "lucide-react";

const CTA_LABEL_CS: Record<string, string> = {
  en: "Get expert guidance",
  es: "Contacta con un experto",
  "zh-cn": "获取专家指导",
};
const FALLBACK_CS_CTA: Record<string, string> = {
  en: "Ready to accelerate your life sciences project? Talk to our experts.",
  es: "¿Listo para acelerar tu proyecto en Life Sciences? Habla con nuestros expertos.",
  "zh-cn": "准备加速您的生命科学项目？与我们的专家交流。",
};

const AUTHORITY_DOMAINS = [
  "europa.eu", "fda.gov", "iso.org", "ema.europa.eu",
  "who.int", "ich.org", "edqm.eu",
];

interface ContentPostPageProps {
  contentType: ContentTypeKey;
}

const isChecklistPost = (post: { slug?: string; title?: string; title_es?: string }) =>
  post?.slug?.toLowerCase().includes("checklist") ||
  post?.title?.toLowerCase().includes("checklist") ||
  post?.title_es?.toLowerCase().includes("checklist") ||
  post?.title_es?.toLowerCase().includes("lista de verificación");


const TABLE_MAP: Record<ContentTypeKey, string> = {
  blog_posts: "blog_posts",
  webinars: "webinars",
  white_papers: "white_papers",
  checklists: "checklists",
  case_studies: "case_studies",
  company_news: "company_news",
  events: "events",
  regulatory_updates: "regulatory_updates",
  podcasts: "podcasts",
};

const ContentPost = ({ contentType }: ContentPostPageProps) => {
  const { slug } = useParams<{ slug: string }>();
  const meta = getContentTypeMeta(contentType);
  const lang = useLanguage();
  const t = uiT(lang);
  const hasEsCols = contentType !== "blog_posts";

  const navigate = useNavigate();
  const location = useLocation();

  const hasZhCols = contentType === "white_papers" || contentType === "checklists" || contentType === "case_studies" || contentType === "company_news";

  const isPreview = new URLSearchParams(location.search).get("preview") === "true";

  const { data: post, isLoading } = useQuery({
    queryKey: ["content-post", contentType, slug, lang, isPreview],
    queryFn: async () => {
      const table = TABLE_MAP[contentType];
      // Try exact slug match first
      let q1 = (supabase as any).from(table).select("*").eq("slug", slug);
      if (!isPreview) q1 = q1.not("published_at", "is", null);
      const { data, error } = await q1.maybeSingle();
      if (error) throw error;
      if (data) {
        // If on ES route but matched by EN slug, redirect to slug_es if available
        if (hasEsCols && lang === "es" && data.slug_es && data.slug_es !== slug) {
          const newPath = location.pathname.replace(`/${slug}`, `/${data.slug_es}`);
          navigate(newPath + location.search, { replace: true });
          return null;
        }
        // If on ZH route but matched by EN slug, redirect to slug_zh if available
        if (hasZhCols && lang === "zh-cn" && data.slug_zh && data.slug_zh !== slug) {
          const newPath = location.pathname.replace(`/${slug}`, `/${data.slug_zh}`);
          navigate(newPath + location.search, { replace: true });
          return null;
        }
        return hasEsCols ? localizeContentRow(data, lang) : data;
      }

      // Fallback: try slug_es for Spanish paths (regulatory_updates, case_studies, etc.)
      if (hasEsCols && lang === "es") {
        let q2 = (supabase as any).from(table).select("*").eq("slug_es", slug);
        if (!isPreview) q2 = q2.not("published_at", "is", null);
        const { data: esData, error: esErr } = await q2.maybeSingle();
        if (esErr) throw esErr;
        if (esData) return localizeContentRow(esData, lang);
      }
      // Fallback: try slug_zh for Chinese paths (white_papers)
      if (hasZhCols && lang === "zh-cn") {
        let q3 = (supabase as any).from(table).select("*").eq("slug_zh", slug);
        if (!isPreview) q3 = q3.not("published_at", "is", null);
        const { data: zhData, error: zhErr } = await q3.maybeSingle();
        if (zhErr) throw zhErr;
        if (zhData) return localizeContentRow(zhData, lang);
      }
      return null;
    },
    enabled: !!slug,
  });


  const cleanedContent = post?.content ? injectInternalLinks(cleanBlogContent(post.content)) : "";
  // Extract subtype from raw content before cleanBlogContent strips leading comments
  const contentSubtype = post?.content?.match(/<!--\s*subtype:\s*(.+?)\s*-->/)?.[1] ?? null;
  // Optional per-article flag: `<!-- hide_author_bio -->` in the content hides the About the Author section
  const hideAuthorBio = /<!--\s*hide_author_bio\s*-->/.test(post?.content ?? "");

  // Extract podcast Host/Guest metadata from content
  const podcastMeta = contentType === "podcasts" && cleanedContent
    ? (() => {
        const hostMatch = cleanedContent.match(/\*\*Host:\*\*\s*(.+)/);
        const guestMatch = cleanedContent.match(/\*\*Guest:\*\*\s*(.+)/);
        return { host: hostMatch?.[1]?.trim(), guest: guestMatch?.[1]?.trim() };
      })()
    : null;

  // Strip Host/Guest lines from podcast content so they're not duplicated
  const podcastCleanedContent = contentType === "podcasts" && cleanedContent
    ? cleanedContent.replace(/\*\*Host:\*\*\s*.+\n?/g, "").replace(/\*\*Guest:\*\*\s*.+\n?/g, "")
    : cleanedContent;
  // Self-hosted videos in the body get stable anchors (#inline-video-1, #inline-video-2, …)
  // so in-text links can jump to and play a specific video.
  const videoSrcs = Array.from(new Set((podcastCleanedContent || "").match(/https?:\/\/[^\s)\]]+?\.(?:mp4|webm)/g) ?? []));
  const videoAnchorFor = (src: string | undefined) => {
    if (!src) return undefined;
    const idx = videoSrcs.findIndex((v) => v === src || src.startsWith(v) || v.startsWith(src));
    return idx >= 0 ? `inline-video-${idx + 1}` : undefined;
  };
  const isCaseStudy = contentType === "case_studies";
  const csCtaHeading = post?.content?.match(/<!--\s*cta_heading:\s*(.+?)\s*-->/)?.[1]?.trim();
  const csCtaBody = post?.content?.match(/<!--\s*cta_body:\s*(.+?)\s*-->/)?.[1]?.trim();
  const csCtaButton = post?.content?.match(/<!--\s*cta_button:\s*(.+?)\s*-->/)?.[1]?.trim();
  const csCtaSecondary = post?.content?.match(/<!--\s*cta_secondary:\s*(.+?)\s*\|\s*(\S+)\s*-->/);
  const caseStudyMeta = isCaseStudy && post?.content ? extractCaseStudyMeta(post.content) : { clientType: undefined, services: undefined, industry: undefined };
  const caseStudySections = isCaseStudy && cleanedContent ? parseCaseStudyContent(cleanedContent.replace(/<!--[\s\S]*?-->/g, "")) : null;

  const aSlug = post?.author ? authorSlug(post.author) : null;
  const { data: authorProfile } = useQuery({
    queryKey: ["author-profile", aSlug],
    queryFn: async () => {
      const { data } = await supabase
        .from("authors")
        .select("slug, linkedin_url")
        .eq("slug", aSlug!)
        .maybeSingle();
      return data;
    },
    enabled: !!aSlug,
  });



  const readingTime = cleanedContent
    ? Math.max(1, Math.ceil(cleanedContent.split(/\s+/).length / 250))
    : 0;

  // Short articles (news) don't need a sidebar ToC
  const headingCount = (cleanedContent.match(/^#{2,3}\s/gm) || []).length;
  const isNewsLike = contentType === "company_news";
  const isShortArticle = isNewsLike || (contentType !== "regulatory_updates" && (readingTime <= 6 || headingCount < 2));
  const isWhitePaper = contentType === "white_papers" || contentType === "checklists";

  // CTA banner for white papers and case studies (same pattern as webinars)
  const wpCtaUrl = (isWhitePaper || contentType === "case_studies" || contentType === "blog_posts") && post?.cta_url ? post.cta_url : null;
  const { data: wpCtaBanner } = useQuery({
    queryKey: ["cta-banner-wp", wpCtaUrl],
    enabled: !!wpCtaUrl,
    queryFn: async () => {
      const { data } = await supabase
        .from("cta_banners")
        .select("title, title_es, description, description_es, image_url, image_alt, cta_label, cta_label_es, entity_href, button_url")
        .eq("entity_href", wpCtaUrl!)
        .order("sort_order", { ascending: true })
        .limit(1)
        .maybeSingle();
      return data;
    },
  });
  const hasDownloadForm = isWhitePaper && !!post?.hubspot_form_id;

  // HowTo schema for procedural white papers / guides
  const howToSteps = isWhitePaper && cleanedContent ? extractHowToSteps(cleanedContent) : null;
  const howToJsonLd = howToSteps && post ? {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: post.title,
    description: post.summary || `A step-by-step guide: ${post.title}`,
    ...(post.featured_image ? { image: post.featured_image } : {}),
    step: howToSteps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  } : null;

  // PodcastEpisode schema for podcast posts
  const podcastJsonLd = contentType === "podcasts" && post ? {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    name: post.title,
    description: post.summary || post.title,
    url: `https://qbdgroup.com/en/podcasts/${post.slug}`,
    ...(post.published_at ? { datePublished: post.published_at } : {}),
    ...(post.featured_image ? { image: post.featured_image } : {}),
    partOfSeries: {
      "@type": "PodcastSeries",
      name: "QbD Group Podcast",
      url: "https://qbdgroup.com/en/podcasts",
    },
    publisher: {
      "@type": "Organization",
      name: "QbD Group",
      url: "https://qbdgroup.com",
    },
  } : null;

  return (
    <div className="min-h-screen">
      <ScrollToTop />
      <Navbar />

      {isLoading ? (
        <ContentLoadingState />
      ) : !post ? (
        <div className="pt-36 pb-20 text-center">
          <h1 className="text-2xl font-bold text-foreground">{t("postNotFound")}</h1>
          <Link to={meta.route} className="mt-4 inline-flex items-center gap-1 text-qbd-blue font-semibold">
            ← {t("backTo")} {meta.label}
          </Link>
        </div>
      ) : (
        <>
          {/* Optional SEO overrides from HTML comments in the content body */}
          {(() => {
            const seoTitle = post.content?.match(/<!--\s*seo_title:\s*(.+?)\s*-->/)?.[1]?.trim();
            const metaDescription = post.content?.match(/<!--\s*meta_description:\s*(.+?)\s*-->/)?.[1]?.trim();
            return (
              <BlogSEO
                type="post"
                title={seoTitle || post.title}
                description={metaDescription || (post.summary ?? undefined)}
                slug={post.slug}
                image={post.featured_image}
                author={post.author ? authorDisplayName(post.author) : undefined}
                authorSlug={authorProfile?.slug}
                authorLinkedin={authorProfile?.linkedin_url}
                publishedAt={post.published_at}
                updatedAt={post.content_updated_at ?? undefined}
                category={post.category}
                tags={post.tags}
                basePath={meta.route}
                breadcrumbLabel={meta.label}
                schemaType={
                  contentType === "white_papers" || contentType === "checklists" ? "TechArticle"
                    : contentType === "regulatory_updates" ? "Article"
                    : contentType === "company_news" ? "NewsArticle"
                    : contentType === "case_studies" ? "Article"
                    : "BlogPosting"
                }
              />
            );
          })()}

          {howToJsonLd && (
            <JsonLd data={howToJsonLd} />
          )}

          {podcastJsonLd && (
            <JsonLd data={podcastJsonLd} />
          )}

          <ReadingProgressBar />

          <ContentPostHero
            title={post.title}
            subtitle={post.content?.match(/<!--\s*subtitle:\s*(.+?)\s*-->/)?.[1] ?? null}
            category={post.category}
            industryCategory={post.industry_category}
            serviceCategory={post.service_category}
            publishedAt={post.published_at}
            author={post.author}
            featuredImage={
              slug === "rescops-e-vlm-rebrands-as-vlmcare"
                ? vlmcareHeroVisual.url
                : post.featured_image
            }
            readingTime={isWhitePaper ? 0 : readingTime}
            summary={post.summary}
            tags={post.tags}
            contentType={contentType}
            caseStudyMeta={isCaseStudy ? caseStudyMeta : undefined}
          />

          {isCaseStudy && caseStudySections ? (
            <>
              <section className="bg-background">
                <div className="container mx-auto px-6 lg:px-10 max-w-3xl pt-8">
                  <ListenButton content={cleanedContent} title={post.title} />
                </div>
              </section>
              <CaseStudyLayout
                sections={caseStudySections}
                meta={caseStudyMeta}
                clientLogo={
                  (post.slug?.includes("techdow") || slug?.includes("techdow"))
                    ? { src: "/__l5e/assets-v1/7bb1de76-a4ca-42b4-9faf-5b65a3996ca3/techdow-logo.png", alt: "Techdow — A Hepalink Company" }
                    : undefined
                }
              />

              {/* Share + Service/Solution CTA */}
              <section className="py-20 sm:py-28 bg-primary relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full border border-primary-foreground/5" />
                  <div className="absolute -bottom-32 -left-16 w-96 h-96 rounded-full border border-primary-foreground/5" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-primary-foreground/[0.03]" />
                </div>
                <div className="container mx-auto px-6 lg:px-10 max-w-3xl relative z-10">
                  {/* CTA content */}
                  <div className="text-center mb-16">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-4">
                      QbD Group
                    </p>
                    <h2 className="text-2xl sm:text-3xl font-bold text-primary-foreground mb-4">
                      {csCtaHeading || post.cta_label || (lang === "es" ? "¿Listo para dar el siguiente paso?" : "Ready to take the next step?")}
                    </h2>
                    <p className="text-base sm:text-lg text-primary-foreground/60 leading-relaxed max-w-xl mx-auto mb-8">
                      {csCtaBody || FALLBACK_CS_CTA[lang] || FALLBACK_CS_CTA.en}
                    </p>
                    <Link
                      to={localizeHref(post.cta_url || "/en/contact", lang)}
                      className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-accent-foreground font-semibold text-base rounded-full hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
                    >
                      {csCtaButton || CTA_LABEL_CS[lang] || CTA_LABEL_CS.en}
                      <ArrowRight size={18} />
                    </Link>
                    {csCtaSecondary && (
                      <div className="mt-5">
                        <a href={csCtaSecondary[2]} className="text-sm font-semibold text-primary-foreground/80 underline underline-offset-4 hover:text-accent">
                          {csCtaSecondary[1]}
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="h-px bg-primary-foreground/10 mb-10" />
                  <ShareButtons url={`${meta.route}/${post.slug}`} title={post.title} />
                </div>
              </section>
            </>
          ) : (
          /* Standard blog/article layout */
          <section className={`bg-background ${hasDownloadForm ? 'py-12 sm:py-16' : 'py-16 sm:py-24'}`}>
            <div className={`container mx-auto px-6 lg:px-10 ${hasDownloadForm ? 'max-w-7xl' : isShortArticle ? 'max-w-3xl' : 'max-w-6xl'}`}>
              <div className={hasDownloadForm ? 'flex flex-col lg:flex-row gap-10 lg:gap-16' : isShortArticle ? '' : 'flex gap-16'}>
                <article className={`flex-1 min-w-0 ${hasDownloadForm ? '' : isShortArticle ? '' : 'max-w-3xl mx-auto xl:mx-0'}`}>
                  {cleanedContent && !isWhitePaper && contentType !== "podcasts" && (
                    <div className="mb-8">
                      <ListenButton content={cleanedContent} title={post.title} />
                    </div>
                  )}

                  {/* White paper: intro highlight box + What you'll learn */}
                  {isWhitePaper && cleanedContent && (() => {
                    // Strip summary from content to avoid duplication with the hero
                    let wpContent = cleanedContent
                      .replace(/<!--[\s\S]*?-->/g, '')
                      .trimStart();

                    if (post.summary) {
                      const normalise = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
                      const normSummary = normalise(post.summary);
                      const firstParaMatch = wpContent.match(/^([^\n]+)\n/);
                      if (firstParaMatch && normalise(firstParaMatch[1]).startsWith(normSummary.slice(0, 60))) {
                        wpContent = wpContent.slice(firstParaMatch[0].length).trimStart();
                      }
                    }

                    // Extract "Key Takeaways" / "What you'll learn" section, accepting DB-escaped/doubled apostrophes
                    const wylHeadingPattern = /## (?:What you['']{1,2}ll learn|Key Takeaways|Puntos clave|Puntos [Cc]lave|要点|关键要点)\s*\n([\s\S]*?)(?=\n## |\n$|$)/i;
                    const wylMatch = wpContent.match(wylHeadingPattern);
                    const wylContent = wylMatch ? wylMatch[1].trim() : null;
                    // Extract disclaimer from HTML comment
                    const disclaimerMatch = cleanedContent.match(/<!--\s*disclaimer:\s*(.+?)\s*-->/);
                    const disclaimer = disclaimerMatch ? disclaimerMatch[1].trim() : null;
                    // contentSubtype is extracted from raw content above so the HTML comment survives cleanBlogContent
                    // Remove "Key Takeaways" / "What you'll learn", "Download" sections from intro content
                    const introContent = wpContent
                      .replace(wylHeadingPattern, '')
                      .replace(/## (?:Download|Descarga|Descárgalo|下载).*\n[\s\S]*/i, '')
                      .trim();

                    if (!introContent && !wylContent) return null;
                    return (
                      <>
                        {/* About this white paper */}
                        {introContent && (
                          <div className="mb-8 p-6 sm:p-8 rounded-2xl bg-secondary/60 border border-border/30">
                            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">{isChecklistPost(post) ? t("aboutChecklist") : contentSubtype === "expert-article" ? t("aboutExpertArticle") : t("aboutWhitePaper")}</h2>
                            <p className="text-sm text-muted-foreground mb-5">{t("keyTopics")}</p>
                            <div className="h-px bg-border/40 mb-6" />
                            <div className="prose prose-base max-w-none prose-p:text-foreground/75 prose-p:leading-[1.8] prose-p:text-[0.9375rem] prose-strong:text-foreground prose-strong:font-semibold prose-li:text-foreground/75 prose-li:leading-[1.8] prose-li:text-[0.9375rem] prose-a:text-qbd-blue [&_ul]:space-y-1.5 [&_ol]:space-y-1.5 [&_ul]:my-3 [&_ol]:my-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:marker:text-primary/60 [&_p+p]:mt-4 prose-img:rounded-xl">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                  h2: () => null,
                                  h3: () => null,
                                  a: ({ href, children }) => (
                                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-qbd-blue hover:underline decoration-accent/40 underline-offset-[3px] transition-colors">
                                      {children}
                                    </a>
                                  ),
                                  img: ({ src, alt }) => (
                                    <figure className="my-6">
                                      <div className="overflow-hidden rounded-xl shadow-md shadow-primary/5">
                                        <img src={src} alt={alt || ""} className="w-full" loading="lazy" />
                                      </div>
                                      {alt && <figcaption className="mt-2 text-center text-xs text-muted-foreground italic">{alt}</figcaption>}
                                    </figure>
                                  ),
                                  ul: ({ children }) => <ul className="space-y-1.5 [&_li]:relative [&_li]:pl-2">{children}</ul>,
                                  ol: ({ children }) => <ol className="space-y-1.5 [&_li]:relative [&_li]:pl-2">{children}</ol>,
                                }}
                              >
                                {introContent}
                              </ReactMarkdown>
                            </div>
                            {disclaimer && (
                              <p className="mt-4 text-xs text-muted-foreground italic">{disclaimer}</p>
                            )}
                          </div>
                        )}

                        {/* Key Takeaways */}
                        {wylContent && (
                          <div className="mb-10 p-6 sm:p-8 rounded-2xl bg-secondary/60 border border-border/30">
                            <h2 className="text-lg sm:text-xl font-bold text-foreground mb-1">
                              {t("keyTakeaways")}
                            </h2>
                            <p className="text-sm text-muted-foreground mb-5">
                              {t("keyTakeawaysDesc")}
                            </p>
                            <div className="h-px bg-border/40 mb-6" />
                            <div className="prose prose-base max-w-none prose-p:text-foreground/75 prose-p:leading-[1.8] prose-p:text-[0.9375rem] prose-li:text-foreground/75 prose-li:leading-[1.8] prose-li:text-[0.9375rem] prose-strong:text-[#002855] prose-strong:font-semibold prose-a:text-qbd-blue [&_ul]:space-y-3 [&_ul]:my-3 [&_ul]:list-none [&_ul]:pl-0 [&_li]:relative [&_li]:pl-7 [&_li]:before:content-['✓'] [&_li]:before:absolute [&_li]:before:left-0 [&_li]:before:text-primary [&_li]:before:font-bold [&_p+p]:mt-4">
                              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {wylContent}
                              </ReactMarkdown>
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}


                  {/* Podcast speakers card */}
                  {contentType === "podcasts" && podcastMeta && (podcastMeta.host || podcastMeta.guest) && (
                    <div className="mb-10 rounded-2xl border border-border/60 bg-gradient-to-br from-muted/40 to-muted/10 p-5 sm:p-6">
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-muted-foreground mb-4">
                        {t("podcastSpeakers")}
                      </p>
                      <div className="flex flex-col sm:flex-row gap-4">
                        {podcastMeta.host && (
                          <div className="flex items-center gap-3 flex-1 rounded-xl bg-background/60 border border-border/40 px-4 py-3">
                            <Mic size={18} className="text-primary shrink-0" strokeWidth={1.5} />
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("podcastHost")}</p>
                              <p className="text-sm font-semibold text-foreground">{podcastMeta.host}</p>
                            </div>
                          </div>
                        )}
                        {podcastMeta.guest && (
                          <div className="flex items-center gap-3 flex-1 rounded-xl bg-background/60 border border-border/40 px-4 py-3">
                            <User size={18} className="text-accent shrink-0" strokeWidth={1.5} />
                            <div>
                              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{t("podcastGuest")}</p>
                              <p className="text-sm font-semibold text-foreground">{podcastMeta.guest}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Regulatory update: structured card layout */}
                  {contentType === "regulatory_updates" && podcastCleanedContent && (
                    <RegulatoryUpdateContent content={podcastCleanedContent} />
                  )}

                  {/* Blog posts: structured card layout */}
                  {contentType === "blog_posts" && podcastCleanedContent && (
                    <BlogPostContent
                      content={podcastCleanedContent}
                      category={post.category}
                      showDropCap={true}
                    />
                  )}

                  {/* Standard content (non white paper, non regulatory update, non blog) */}
                  {!isWhitePaper && contentType !== "regulatory_updates" && contentType !== "blog_posts" && (
                  <div className={`prose prose-lg max-w-none prose-headings:text-foreground prose-headings:scroll-mt-36 prose-p:text-foreground/80 prose-p:leading-[1.85] prose-p:text-[1.0625rem] prose-a:text-qbd-blue prose-a:decoration-qbd-blue/30 prose-a:underline-offset-[3px] prose-a:transition-colors hover:prose-a:decoration-qbd-blue prose-strong:text-foreground prose-strong:font-bold prose-table:border-border prose-th:bg-muted prose-img:rounded-xl prose-li:text-foreground/80 prose-li:leading-[1.85] prose-li:text-[1.0625rem] prose-hr:border-border/60 [&_h2]:text-[1.625rem] [&_h2]:sm:text-[1.875rem] [&_h2]:font-bold [&_h2]:mt-16 [&_h2]:mb-6 [&_h2]:pb-4 [&_h2]:border-b [&_h2]:border-border/40 [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-12 [&_h3]:mb-5 [&_ul]:my-7 [&_ol]:my-7 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:marker:text-primary/60 [&_p+p]:mt-6 [&_table]:rounded-xl [&_table]:overflow-hidden [&_th]:px-4 [&_th]:py-3 [&_th]:text-left [&_th]:text-sm [&_th]:font-semibold [&_td]:px-4 [&_td]:py-3 [&_td]:text-sm [&_td]:border-b [&_td]:border-border/40 prose-blockquote:border-l-[3px] prose-blockquote:border-primary/30 prose-blockquote:bg-primary/[0.03] prose-blockquote:rounded-r-2xl prose-blockquote:py-5 prose-blockquote:px-7 prose-blockquote:not-italic prose-blockquote:text-foreground/75 prose-blockquote:text-[1.125rem] prose-blockquote:leading-[1.7] prose-blockquote:font-medium prose-blockquote:my-10 [&_blockquote_p]:before:content-none [&_blockquote_p]:after:content-none [&_blockquote_span.inline-block]:mb-0 ${!isNewsLike && contentType !== "podcasts" ? '[&>p:first-of-type]:first-letter:text-[3.5rem] [&>p:first-of-type]:first-letter:font-bold [&>p:first-of-type]:first-letter:text-primary [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-3 [&>p:first-of-type]:first-letter:leading-[0.8] [&>p:first-of-type]:first-letter:mt-2' : ''}`}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        h2: ({ children, ...props }) => {
                          const text = typeof children === "string" ? children : String(children);
                          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                          return <h2 id={id} {...props}>{children}</h2>;
                        },
                        h3: ({ children, ...props }) => {
                          const text = typeof children === "string" ? children : String(children);
                          const id = text.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                          return <h3 id={id} {...props}>{children}</h3>;
                        },
                        a: ({ href, children }) => {
                          // In-text links to a self-hosted video anchor: smooth-scroll and play from the start
                          if (href && /^#inline-video-\d+$/.test(href)) {
                            return (
                              <a
                                href={href}
                                onClick={(e) => {
                                  e.preventDefault();
                                  const target = document.getElementById(href.slice(1));
                                  if (!target) return;
                                  target.scrollIntoView({ behavior: "smooth", block: "center" });
                                  const video = target.querySelector("video");
                                  if (video) {
                                    video.currentTime = 0;
                                    video.play().catch(() => {});
                                  }
                                }}
                                className="text-qbd-blue hover:underline decoration-accent/40 underline-offset-[3px] transition-colors"
                              >
                                {children}
                              </a>
                            );
                          }
                          const isAuthority = href
                            ? AUTHORITY_DOMAINS.some((d) => new URL(href, "https://x.com").hostname.endsWith(d))
                            : false;
                          return (
                            <a href={href} target="_blank" rel="noopener noreferrer" className="text-qbd-blue hover:underline decoration-accent/40 underline-offset-[3px] transition-colors">
                              {children}
                              {isAuthority && <ExternalLink size={12} className="inline ml-1 -mt-0.5 opacity-60" />}
                            </a>
                          );
                        },
                        img: ({ src, alt }) => {
                          // Self-hosted video file — render an inline player
                          if (src && /\.(mp4|webm)(\?|#|$)/.test(src)) {
                            return (
                              <figure id={videoAnchorFor(src)} className="my-10 mx-0 not-prose scroll-mt-32">
                                <div className="overflow-hidden rounded-2xl shadow-xl shadow-primary/10 bg-black">
                                  <video src={src} controls playsInline preload="metadata" className="w-full aspect-video" />
                                </div>
                                {alt && <figcaption className="mt-3 text-center text-xs text-muted-foreground italic">{alt}</figcaption>}
                              </figure>
                            );
                          }
                          const isVlmcareMockup = slug === "rescops-e-vlm-rebrands-as-vlmcare" && (src?.includes("vlmcare") || src?.includes("laptop") || src?.includes("banner"));
                          if (isVlmcareMockup) {
                            return (
                              <figure className="my-10 max-w-3xl mx-auto">
                                <a href="https://vlmcare.com/" target="_blank" rel="noopener noreferrer" className="block group">
                                  <img src={src} alt={alt || ""} className="w-full transition-transform duration-300 group-hover:scale-[1.01]" loading="lazy" />
                                </a>
                                {alt && <figcaption className="mt-3 text-center text-xs text-muted-foreground italic">{alt}</figcaption>}
                              </figure>
                            );
                          }
                          return (
                            <figure className="my-10 -mx-4 sm:mx-0">
                              <div className="overflow-hidden rounded-2xl shadow-lg shadow-primary/5">
                                <img src={src} alt={alt || ""} className="w-full" loading="lazy" />
                              </div>
                              {alt && <figcaption className="mt-3 text-center text-xs text-muted-foreground italic">{alt}</figcaption>}
                            </figure>
                          );
                        },

                        ul: ({ children, node }) => {
                          // A list where every item is "**m:ss** – title" becomes an interactive chapter index for the video above it.
                          const hastText = (n: any): string =>
                            n?.type === "text" ? n.value : (n?.children || []).map(hastText).join("");
                          const items = ((node as any)?.children || []).filter((c: any) => c.type === "element" && c.tagName === "li");
                          const chapters = items.map((li: any) => {
                            const m = hastText(li).trim().match(/^(\d{1,2}(?::\d{2}){1,2})\s*[–—-]\s*([\s\S]+)$/);
                            return m ? { time: m[1], title: m[2].trim() } : null;
                          });
                          if (chapters.length > 1 && chapters.every(Boolean)) {
                            return <VideoChapters chapters={chapters as { time: string; title: string }[]} />;
                          }
                          return <ul className="space-y-2 [&_li]:relative [&_li]:pl-2">{children}</ul>;
                        },
                        ol: ({ children }) => <ol className="space-y-2 [&_li]:relative [&_li]:pl-2">{children}</ol>,
                        blockquote: ({ children, ...props }) => {
                          const extractText = (nodes: React.ReactNode): string => {
                            if (typeof nodes === "string" || typeof nodes === "number") return String(nodes);
                            if (!nodes) return "";
                            if (Array.isArray(nodes)) return nodes.map(extractText).join("");
                            const childProps = (nodes as React.ReactElement<any>)?.props;
                            if (childProps?.children) return extractText(childProps.children);
                            return "";
                          };
                          const raw = extractText(children);
                          const isBjorn = raw.includes("Bjorn Aalbers") || raw.includes("Björn Aalbers");
                          if (!isBjorn) {
                            const childArray = React.Children.toArray(children).filter(
                              (c) => !(typeof c === "string" && !c.trim())
                            );
                            const last = childArray[childArray.length - 1];
                            const isAttr = /^[—–-]\s*/.test(extractText(last).trim());
                            const attribution = isAttr ? last : null;
                            const body = isAttr ? childArray.slice(0, -1) : childArray;
                            return <PullQuote attribution={attribution}>{body}</PullQuote>;
                          }
                          return (
                            <blockquote {...props} className="not-prose my-10 rounded-2xl border border-primary/15 bg-primary/[0.04] p-6 sm:p-8">
                              <div className="flex flex-col sm:flex-row gap-6 items-start">
                                <img
                                  src={bjornAalbersPhoto.url}
                                  alt="Björn Aalbers"
                                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover shrink-0 ring-2 ring-primary/20"
                                  loading="lazy"
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="text-foreground/80 text-[1.0625rem] leading-[1.75]">{children}</div>
                                  <a
                                    href="https://www.linkedin.com/in/bj%C3%B6rn-aalbers/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-qbd-blue hover:text-qbd-blue/80 transition-colors"
                                  >
                                    <Linkedin size={16} /> Connect on LinkedIn
                                  </a>
                                </div>
                              </div>
                            </blockquote>
                          );
                        },
                      }}
                    >
                      {podcastCleanedContent}
                    </ReactMarkdown>
                  </div>
                  )}

                  {/* Podcast video embed */}
                  {contentType === "podcasts" && post.video_url && (() => {
                    const vimeoMatch = post.video_url.match(/vimeo\.com\/(\d+)/);
                    const vimeoId = vimeoMatch ? vimeoMatch[1] : null;
                    return vimeoId ? (
                      <div className="mt-14">
                        <div className="relative w-full rounded-2xl overflow-hidden shadow-xl shadow-primary/10" style={{ paddingBottom: '56.25%' }}>
                          <iframe
                            src={`https://player.vimeo.com/video/${vimeoId}?byline=0&portrait=0`}
                            className="absolute inset-0 w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                            title={post.title}
                          />
                        </div>
                      </div>
                    ) : null;
                  })()}

                  {contentType === "podcasts" && post.speakers?.length > 0 ? (
                    <div className="mt-16 space-y-6">
                      {post.speakers.map((speaker: string) => (
                        <AuthorBioBox key={speaker} authorRaw={speaker} />
                      ))}
                    </div>
                  ) : post.author && !isNewsLike && !hideAuthorBio ? (
                    <div className="mt-16 space-y-6">
                      {parseAuthors(post.author).map(a => (
                        <AuthorBioBox key={authorSlug(a)} authorRaw={a} />
                      ))}
                    </div>
                  ) : null}


                  {/* Contextual CTA — appears after article body for conversion */}
                  {!isWhitePaper && !isNewsLike && (
                    <InlineCTA
                      category={post.service_category || post.category}
                      ctaLabel={post.cta_label}
                      ctaUrl={post.cta_url}
                    />
                  )}

                  {/* Company News CTA */}
                  {isNewsLike && (() => {
                    const isVlmcareArticle = slug === "rescops-e-vlm-rebrands-as-vlmcare";
                    if (isVlmcareArticle) {
                      return (
                        <div className="mt-16 py-14 px-8 sm:px-12 rounded-2xl bg-primary text-center relative overflow-hidden">
                          <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-primary-foreground/5" />
                          <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full border border-primary-foreground/5" />
                          <div className="relative z-10">
                            <img
                              src={vlmcareLogoWhite.url}
                              alt="VLMcare"
                              className="mx-auto mb-6 h-16 w-auto"
                            />
                            <h3 className="text-xl sm:text-2xl font-bold text-primary-foreground mb-3">
                              Validation lifecycle management, simplified.
                            </h3>
                            <p className="text-base text-primary-foreground/60 max-w-md mx-auto mb-6">
                              VLMcare gives life sciences teams one platform to manage, track, and close validation activities from planning to approval.
                            </p>
                            <a
                              href="https://www.vlmcare.com"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-accent-foreground font-semibold rounded-full hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
                            >
                              Explore VLMcare
                              <ArrowRight size={16} />
                            </a>
                          </div>
                        </div>
                      );
                    }
                    const ctaHeading = lang === "en"
                      ? ((post as any).cta_label || "Need expert guidance?")
                      : (lang === "es" ? "¿Necesitas ayuda de expertos?" : "Need expert guidance?");
                    const ctaDesc = (post as any).cta_url
                      ? (lang === "es"
                        ? "Descubre cómo QbD Group puede ayudarte a transformar tus operaciones."
                        : "Discover how QbD Group can help you transform your safety operations.")
                      : (lang === "es"
                        ? "Nuestros expertos están listos para ayudarle con su proyecto Life Sciences."
                        : "Our experts are ready to help you with your life sciences project.");
                    const ctaHref = (post as any).cta_url
                      ? localizeHref((post as any).cta_url, lang)
                      : localizeHref("/en/contact", lang);
                    return (
                      <div className="mt-16 py-14 px-8 sm:px-12 rounded-2xl bg-primary text-center relative overflow-hidden">
                        <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full border border-primary-foreground/5" />
                        <div className="absolute -bottom-20 -left-12 w-64 h-64 rounded-full border border-primary-foreground/5" />
                        <div className="relative z-10">
                          <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent mb-3">QbD Group</p>
                          <h3 className="text-xl sm:text-2xl font-bold text-primary-foreground mb-3">
                            {ctaHeading}
                          </h3>
                          <p className="text-base text-primary-foreground/60 max-w-md mx-auto mb-6">
                            {ctaDesc}
                          </p>
                          <Link
                            to={ctaHref}
                            className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent text-accent-foreground font-semibold rounded-full hover:bg-accent/90 transition-colors shadow-lg shadow-accent/20"
                          >
                            {lang === "es" ? "Contáctanos" : "Get in touch"}
                            <ArrowRight size={16} />
                          </Link>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Cross-content cards — links to related whitepapers/case studies */}
                  {!isWhitePaper && contentType !== "regulatory_updates" && post.id && (
                    <CrossContentCards
                      category={post.category}
                      serviceCategory={post.service_category}
                      industryCategory={post.industry_category}
                      currentId={post.id}
                      currentType={contentType}
                      currentSlug={post.slug}
                    />
                  )}

                  <div className={`${isWhitePaper ? 'mt-8' : 'mt-12'} pt-8 border-t border-border/50`}>
                    <ShareButtons url={`${meta.route}/${post.slug}`} title={post.title} />
                  </div>

                  {!isWhitePaper && (
                    <div className="mt-16">
                      <NewsletterSignup />
                    </div>
                  )}
                </article>

                {/* TOC sidebar — right side (matching BlogPost layout) */}
                {!isShortArticle && !hasDownloadForm && cleanedContent && (
                  <aside className="hidden xl:block w-64 shrink-0">
                    <div className="sticky top-36 space-y-6">
                      <TableOfContents
                        content={cleanedContent}
                        lang={lang}
                        backUrl={meta.route}
                        backLabel={meta.label}
                      />
                    </div>
                  </aside>
                )}

                {/* White paper download form sidebar */}
                {hasDownloadForm && (
                  <aside className="w-full lg:w-[420px] shrink-0 order-first lg:order-last">
                    <div className="sticky top-28">
                      <WebinarRegistrationForm
                        registrationUrl={post.download_url}
                        title={post.title}
                        hubspotFormId={post.hubspot_form_id}
                        heading={isChecklistPost(post) ? t("downloadChecklist") : t("downloadWhitePaper")}
                        subtitle={t("fillFormAccess")}
                      />
                    </div>
                  </aside>
                )}
              </div>
            </div>
          </section>
          )}

          {/* White paper / case study CTA banner above related content */}
          {(isWhitePaper || isCaseStudy || contentType === "blog_posts") && wpCtaBanner && (() => {
            const isSpanish = lang === "es";
            const ctaImage = wpCtaBanner.image_url || post.featured_image || null;
            const ctaDescription = (isSpanish ? wpCtaBanner.description_es : null) || wpCtaBanner.description || '';
            const ctaTitle = (isSpanish ? wpCtaBanner.title_es : null) || wpCtaBanner.title || (isSpanish ? "Descubre cómo podemos ayudarte" : "Discover how we can help");
            const ctaLbl = (isSpanish ? wpCtaBanner.cta_label_es : null) || wpCtaBanner.cta_label || post.cta_label || "Read more";
            if (!ctaImage) return null;
            return (
              <SplitBanner
                title={ctaTitle}
                description={ctaDescription.replace(/\*\*/g, '')}
                image={ctaImage}
                imageAlt={wpCtaBanner.image_alt || ctaLbl}
                ctaLabel={ctaLbl}
                onCtaClick={() => { window.location.href = wpCtaBanner?.button_url ? localizeHref(wpCtaBanner.button_url, lang) : localizeHref(post.cta_url || "/en/contact", lang); }}
              />
            );
          })()}

          {contentType !== "company_news" && (
            <RelatedContent currentId={post.id} category={post.category} industryCategory={post.industry_category} serviceCategory={post.service_category} tags={post.tags} contentType={contentType} {...(isWhitePaper ? { title: isChecklistPost(post) ? t("moreChecklists") : t("moreWhitePapers") } : {})} />
          )}
        </>
      )}

      <Footer />
    </div>
  );
};

export default ContentPost;
